/* ═══════════════════════════════════════════════════
   Step 1 — Overview
   Campaign details + geo + platform KPIs + creative
   ═══════════════════════════════════════════════════ */
import React from 'react';
import { KpiRow } from './SharedUI';
import TentpoleMomentsSection from './TentpoleMomentsSection';
import { useBrandProfile } from '../../contexts/BrandProfileContext';
import {
  TrendingUp, Heart, Check, DollarSign, MapPin, BarChart3, Info, Eye,
  Save, ArrowRight, Loader2, FileText, ArrowRightCircle, ChevronDown, CalendarDays,
} from 'lucide-react';
import {
  T, P, BG, CARD, BORDER,
  LUMINA_SKUS, PLATFORM_OPTIONS, COMING_SOON_PLATFORMS, GEO_LEVELS, US_STATES, TOP_DMAS,
  PERF_KPIS, BRAND_KPIS, PERF_DELIVERABLES, CREATIVE_ANGLES,
  formatGeoTarget,
} from './constants';
import { budgetHint } from './calibrationProfiles';
import SkuPicker from './SkuPicker';
import IncentiveBuilder from './IncentiveBuilder';
import { useIsDemoBrand } from '../../lib/demoBrand';
import { useDemoProfile } from '../../hooks/useDemoProfile';

/* ──────────────────────────────────────────────
   Sub-panels — keep each component small & focused
   ────────────────────────────────────────────── */

function CommonFields({ state, actions, selectedSKUObj }) {
  const { campaignName, selectedSku, campaignGoal, budget, startDate, endDate, submissionDeadline, waveType } = state;
  const { skus: brandSkus } = useBrandProfile();
  const isDemoBrand = useIsDemoBrand();
  const { profile: demoProfile, isDemo } = useDemoProfile();
  const skuOptions = (brandSkus && brandSkus.length > 0)
    ? brandSkus.map((s, i) => ({ id: s.id || `brand-sku-${i}`, name: s.name, category: s.category || '', image: s.image }))
    // Demo brands receive a brand-themed SKU palette (Helix cars, Grandburger
    // menu items, ShineWash services). Falls back to legacy Lumina demo only
    // for the seeded admin account.
    : (isDemo && demoProfile?.build_wave_skus?.length)
      ? demoProfile.build_wave_skus
      : (isDemoBrand ? LUMINA_SKUS : []);
  const placeholder = (isDemo && demoProfile?.build_wave_campaign_placeholder)
    ? demoProfile.build_wave_campaign_placeholder
    : 'e.g., Summer Hydration Performance Wave – Q2 2026';
  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Campaign Name <span className="text-red-400">*</span></label>
        <input type="text" value={campaignName} onChange={e => actions.setCampaignName(e.target.value)} placeholder={placeholder}
          className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-all" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="campaign-name-input" />
      </div>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-2">Product / SKU <span className="text-red-400">*</span></label>
        <SkuPicker skus={skuOptions} selectedSkuId={selectedSku} onSelect={(id) => actions.setSelectedSku(id)} />
        {selectedSKUObj && (
          <div className="flex items-center gap-3 mt-3 p-2.5 rounded-lg" style={{ background: BG, border: `1px solid ${T}30` }}>
            {selectedSKUObj.image && <img src={selectedSKUObj.image} alt="" className="w-12 h-8 rounded object-cover" />}
            <div className="flex-1">
              <p className="text-[11px] font-bold text-white">{selectedSKUObj.name}</p>
              <p className="text-[9px] text-gray-600">{selectedSKUObj.category}</p>
            </div>
            <Check className="h-4 w-4" style={{ color: T }} />
          </div>
        )}
      </div>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Campaign Overview <span className="text-red-400">*</span></label>
        <textarea value={campaignGoal} onChange={e => actions.setCampaignGoal(e.target.value)} rows={3}
          placeholder="Describe the campaign objective and key messaging in your own words..."
          className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 resize-none focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="campaign-goal-input" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Budget <span className="text-red-400">*</span></label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
            <input type="number" value={budget} onChange={e => actions.setBudget(e.target.value)}
              className="w-full pl-8 pr-3 py-2.5 rounded-lg text-sm text-white focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="budget-input" />
          </div>
          <p className="text-[8px] text-gray-600 mt-1">{budgetHint(waveType)}</p>
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Flight Dates</label>
          <div className="flex gap-1.5">
            <input type="date" value={startDate} onChange={e => actions.setStartDate(e.target.value)} className="flex-1 px-2 py-2.5 rounded-lg text-[11px] text-white focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="start-date" />
            <input type="date" value={endDate} onChange={e => actions.setEndDate(e.target.value)} className="flex-1 px-2 py-2.5 rounded-lg text-[11px] text-white focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="end-date" />
          </div>
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Submission Deadline</label>
          <input type="date" value={submissionDeadline} onChange={e => actions.setSubmissionDeadline(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-[11px] text-white focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="submission-deadline" />
          <p className="text-[8px] text-gray-600 mt-1">Default: 7 days before flight start</p>
        </div>
      </div>
    </div>
  );
}

function GeoTargetingSection({ state, actions }) {
  const { geoLevel, selectedStates, selectedDmas, zipCodes } = state;
  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="geo-targeting-section">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4" style={{ color: T }} />
        <h3 className="text-xs font-bold text-white">Geo-Targeting</h3>
        <span className="text-[8px] text-gray-600 ml-auto">Define where your cohort creators and audience are located</span>
      </div>

      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-2">Geographic Scope</label>
        <div className="grid grid-cols-4 gap-2">
          {GEO_LEVELS.map(level => (
            <button key={level} onClick={() => {
              actions.setGeoLevel(level);
              if (level === 'National') { actions.setSelectedStates([]); actions.setSelectedDmas([]); actions.setZipCodes(''); }
            }}
              className="px-3 py-2.5 rounded-lg text-[11px] font-semibold text-center transition-all"
              style={{ background: geoLevel === level ? `${T}15` : BG, color: geoLevel === level ? T : '#666', border: `1px solid ${geoLevel === level ? `${T}50` : BORDER}` }}
              data-testid={`geo-${level.toLowerCase().replace(/\s/g, '-')}`}>
              {level}
            </button>
          ))}
        </div>
      </div>

      {geoLevel === 'National' && (
        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: `${T}08`, border: `1px solid ${T}20` }}>
          <MapPin className="h-4 w-4 flex-shrink-0" style={{ color: T }} />
          <div>
            <p className="text-[11px] font-semibold text-white">Full US National Coverage</p>
            <p className="text-[9px] text-gray-500">Creators will be matched from all 50 states and 210 DMAs</p>
          </div>
        </div>
      )}

      {geoLevel === 'State' && (
        <div className="space-y-2">
          <label className="text-[9px] text-gray-500 uppercase font-semibold block">Select Target States</label>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="state-selector">
            {US_STATES.map(st => {
              const sel = selectedStates.includes(st);
              return (
                <button key={st} onClick={() => actions.setSelectedStates(prev => sel ? prev.filter(s => s !== st) : [...prev, st])}
                  className="px-2 py-1 rounded text-[9px] font-semibold transition-all"
                  style={{ background: sel ? `${T}18` : 'transparent', color: sel ? T : '#555', border: `1px solid ${sel ? `${T}40` : BORDER}` }}>
                  {st}
                </button>
              );
            })}
          </div>
          {selectedStates.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-[9px]" style={{ color: T }}>{selectedStates.length} state{selectedStates.length !== 1 ? 's' : ''} selected</p>
              <button onClick={() => actions.setSelectedStates([])} className="text-[8px] text-gray-600 hover:text-white transition-all">Clear</button>
            </div>
          )}
        </div>
      )}

      {geoLevel === 'DMA' && (
        <div className="space-y-2">
          <label className="text-[9px] text-gray-500 uppercase font-semibold block">Select Designated Market Areas</label>
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="dma-selector">
            {TOP_DMAS.map(dma => {
              const sel = selectedDmas.includes(dma);
              return (
                <button key={dma} onClick={() => actions.setSelectedDmas(prev => sel ? prev.filter(d => d !== dma) : [...prev, dma])}
                  className="px-2.5 py-1.5 rounded-lg text-[9px] font-semibold transition-all"
                  style={{ background: sel ? `${T}18` : 'transparent', color: sel ? T : '#555', border: `1px solid ${sel ? `${T}40` : BORDER}` }}>
                  {dma}
                </button>
              );
            })}
          </div>
          {selectedDmas.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-[9px]" style={{ color: T }}>{selectedDmas.length} DMA{selectedDmas.length !== 1 ? 's' : ''} selected</p>
              <button onClick={() => actions.setSelectedDmas([])} className="text-[8px] text-gray-600 hover:text-white transition-all">Clear</button>
            </div>
          )}
        </div>
      )}

      {geoLevel === 'Zip Code' && (
        <div className="space-y-2">
          <label className="text-[9px] text-gray-500 uppercase font-semibold block">Enter Zip Codes</label>
          <textarea value={zipCodes} onChange={e => actions.setZipCodes(e.target.value)}
            placeholder="Enter zip codes separated by commas or one per line (e.g., 10001, 90210, 60601...)"
            className="w-full px-3 py-2.5 rounded-lg text-xs text-white placeholder-gray-600 resize-none focus:outline-none" rows={3}
            style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="zip-code-input" />
          {zipCodes && (
            <p className="text-[9px]" style={{ color: T }}>{zipCodes.split(/[,\n]/).filter(z => z.trim()).length} zip code{zipCodes.split(/[,\n]/).filter(z => z.trim()).length !== 1 ? 's' : ''} entered</p>
          )}
        </div>
      )}
    </div>
  );
}

function PlatformPicker({ platforms, togglePlatform, accent }) {
  return (
    <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
      <h3 className="text-xs font-bold text-white">Target Platforms <span className="text-red-400">*</span></h3>
      <div className="flex gap-2 flex-wrap">
        {PLATFORM_OPTIONS.map(p => (
          <button key={p} onClick={() => togglePlatform(p)}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{ background: platforms.includes(p) ? `${accent}18` : BG, color: platforms.includes(p) ? accent : '#666', border: `1px solid ${platforms.includes(p) ? `${accent}50` : BORDER}` }}
            data-testid={`platform-${p.toLowerCase()}`}>{p}</button>
        ))}
        {COMING_SOON_PLATFORMS.map(p => (
          <button key={p} disabled
            className="px-4 py-2 rounded-lg text-xs font-semibold opacity-40 cursor-not-allowed"
            style={{ background: BG, color: '#555', border: `1px dashed ${BORDER}` }}
            title="No modeled creators on this platform yet"
            data-testid={`platform-${p.toLowerCase()}-coming-soon`}>{p} · coming soon</button>
        ))}
      </div>
    </div>
  );
}

function IncentivesPanel({ state, actions }) {
  const { commissionPct, customCommission, boostTrack, boostFields } = state;
  const incentive = {
    commission_pct: commissionPct || '10%',
    custom_commission: customCommission || '',
    boost_track: boostTrack || 'none',
    boost_fields: boostFields || {},
  };
  const handleIncentiveChange = (next) => {
    actions.setCommissionPct(next.commission_pct);
    if (actions.setCustomCommission) actions.setCustomCommission(next.custom_commission);
    if (actions.setBoostTrack) actions.setBoostTrack(next.boost_track);
    if (actions.setBoostFields) actions.setBoostFields(next.boost_fields);
  };
  return <IncentiveBuilder incentive={incentive} onChange={handleIncentiveChange} />;
}

function PerformanceKpisDeliverables({ state, actions, estCreators }) {
  const { platforms, openPlatformTab, enabledPerfKpis, selectedDeliverables } = state;

  return (
    <>
      {platforms.length > 0 ? (
        <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${T}25` }} data-testid="platform-kpis-perf">
          <h3 className="text-xs font-bold text-white flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5" style={{ color: T }} />Native Performance KPIs by Platform — Set Your Targets
          </h3>
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: BG }}>
            {platforms.map(p => (
              <button key={p} onClick={() => actions.setOpenPlatformTab(p)}
                className="flex-1 px-3 py-2 rounded-md text-[10px] font-semibold transition-all"
                style={{ background: openPlatformTab === p ? `${T}15` : 'transparent', color: openPlatformTab === p ? T : '#666' }}
                data-testid={`kpi-tab-${p.toLowerCase()}`}>{p}</button>
            ))}
          </div>
          {openPlatformTab && PERF_KPIS[openPlatformTab] && (
            <div className="space-y-1.5" data-testid={`kpi-list-${openPlatformTab.toLowerCase()}`}>
              {PERF_KPIS[openPlatformTab].map(kpi => (
                <KpiRow key={kpi.name} kpi={kpi} enabled={enabledPerfKpis[`${openPlatformTab}::${kpi.name}`] ?? kpi.required}
                  onToggle={() => actions.togglePerfKpi(openPlatformTab, kpi.name)} />
              ))}
            </div>
          )}
          <p className="text-[8px] text-gray-600 flex items-center gap-1"><Info className="h-3 w-3" style={{ color: T }} />Estimated minimum creators needed: <span className="font-bold text-white">{estCreators}</span> (based on budget & KPIs)</p>
        </div>
      ) : (
        <p className="text-[10px] text-gray-600 px-5 pt-4">Pick at least one target platform above to set native KPI targets.</p>
      )}

      <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
        <h3 className="text-xs font-bold text-white">Deliverables</h3>
        <p className="text-[9px] text-gray-500">Select the media units creators will produce</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="deliverables-grid">
          {PERF_DELIVERABLES.map(d => {
            const sel = selectedDeliverables.has(d.title);
            return (
              <button key={d.title} onClick={() => actions.setSelectedDeliverables(prev => {
                const n = new Set(prev);
                if (n.has(d.title)) n.delete(d.title); else n.add(d.title);
                return n;
              })}
                className="p-3 rounded-lg text-left transition-all" style={{ background: sel ? `${T}10` : BG, border: `1px solid ${sel ? `${T}40` : BORDER}` }}>
                <d.icon className="h-4 w-4 mb-1.5" style={{ color: sel ? T : '#555' }} />
                <p className="text-[10px] font-bold" style={{ color: sel ? '#fff' : '#888' }}>{d.title}</p>
                <p className="text-[8px] text-gray-600 mt-0.5">{d.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function BrandingKpiList({ platform, enabledBrandKpis, toggleBrandKpi }) {
  if (!BRAND_KPIS[platform]) return null;
  return (
    <div className="space-y-2">
      <p className="text-[8px] text-gray-600 uppercase font-semibold">Core Metrics</p>
      {BRAND_KPIS[platform].core.map(k => (
        <div key={k} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }}>
          <input type="checkbox" checked={enabledBrandKpis[`${platform}::${k}`] ?? true} onChange={() => toggleBrandKpi(platform, k)}
            className="w-3.5 h-3.5 rounded" style={{ accentColor: P }} />
          <span className="text-[11px] font-semibold text-white flex-1">{k}</span>
          <input type="text" placeholder="Target" className="w-20 px-2 py-1 rounded text-[10px] text-white placeholder-gray-600 focus:outline-none" style={{ background: CARD, border: `1px solid ${BORDER}` }} />
        </div>
      ))}
      <p className="text-[8px] text-gray-600 uppercase font-semibold mt-2">Platform-Specific</p>
      {BRAND_KPIS[platform].specific.map(k => (
        <div key={k} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }}>
          <input type="checkbox" checked={enabledBrandKpis[`${platform}::${k}`] ?? false} onChange={() => toggleBrandKpi(platform, k)}
            className="w-3.5 h-3.5 rounded" style={{ accentColor: P }} />
          <span className="text-[11px] text-gray-400 flex-1">{k}</span>
          <input type="text" placeholder="Target" className="w-20 px-2 py-1 rounded text-[10px] text-white placeholder-gray-600 focus:outline-none" style={{ background: CARD, border: `1px solid ${BORDER}` }} />
        </div>
      ))}
    </div>
  );
}

function BrandingSection({ state, actions }) {
  const { platforms, openBrandTab, enabledBrandKpis, primaryCreative, secondaryCreatives, creativeNotes } = state;
  const handleCreativeClick = (title) => {
    if (primaryCreative === title) { actions.setPrimaryCreative(''); return; }
    if (!primaryCreative) { actions.setPrimaryCreative(title); return; }
    actions.setSecondaryCreatives(prev => {
      const n = new Set(prev);
      if (n.has(title)) n.delete(title); else n.add(title);
      return n;
    });
  };
  return (
    <>
      {platforms.length > 0 ? (
        <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${P}25` }} data-testid="platform-kpis-brand">
          <h3 className="text-xs font-bold text-white flex items-center gap-2">
            <Eye className="h-3.5 w-3.5" style={{ color: P }} />Native Awareness KPIs by Platform — Set Your Targets
          </h3>
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: BG }}>
            {platforms.map(p => (
              <button key={p} onClick={() => actions.setOpenBrandTab(p)}
                className="flex-1 px-3 py-2 rounded-md text-[10px] font-semibold transition-all"
                style={{ background: openBrandTab === p ? `${P}15` : 'transparent', color: openBrandTab === p ? P : '#666' }}>{p}</button>
            ))}
          </div>
          {openBrandTab && <BrandingKpiList platform={openBrandTab} enabledBrandKpis={enabledBrandKpis} toggleBrandKpi={actions.toggleBrandKpi} />}
        </div>
      ) : (
        <p className="text-[10px] text-gray-600 px-5 pt-4">Pick at least one target platform above to set native KPI targets.</p>
      )}

      <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
        <h3 className="text-xs font-bold text-white">Creative Direction</h3>
        <p className="text-[9px] text-gray-500">Select one primary angle + any secondary angles</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="creative-direction-grid">
          {CREATIVE_ANGLES.map(a => {
            const isPrimary = primaryCreative === a.title;
            const isSecondary = secondaryCreatives.has(a.title);
            const sel = isPrimary || isSecondary;
            return (
              <button key={a.title} onClick={() => handleCreativeClick(a.title)}
                className="p-3 rounded-lg text-left transition-all relative" style={{ background: sel ? `${a.color}10` : BG, border: `1px solid ${sel ? `${a.color}40` : BORDER}` }}>
                {isPrimary && <span className="absolute top-2 right-2 text-[7px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${T}20`, color: T }}>PRIMARY</span>}
                {isSecondary && <span className="absolute top-2 right-2 text-[7px] px-1.5 py-0.5 rounded font-bold text-gray-500 bg-white/5">SECONDARY</span>}
                <p className="text-[10px] font-bold pr-16" style={{ color: sel ? '#fff' : '#888' }}>{a.title}</p>
                <p className="text-[8px] text-gray-600 mt-1">{a.desc}</p>
              </button>
            );
          })}
        </div>
        <textarea value={creativeNotes} onChange={e => actions.setCreativeNotes(e.target.value)} placeholder="Additional creative notes (optional)..." rows={2}
          className="w-full px-3 py-2 rounded-lg text-xs text-white placeholder-gray-600 resize-none focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="creative-notes" />
      </div>
    </>
  );
}

function FineTune({ title, icon: Icon, summary, accent = T, testId, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="rounded-xl overflow-hidden transition-colors" style={{ background: CARD, border: `1px solid ${open ? `${accent}30` : BORDER}` }} data-testid={testId}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`${testId}-toggle`}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}12` }}>
          <Icon className="h-4 w-4" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-white">{title}</p>
          {summary && <p className="text-[10px] text-gray-500 truncate mt-0.5">{summary}</p>}
        </div>
        <ChevronDown className="h-4 w-4 text-gray-600 flex-shrink-0 transition-transform duration-200" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && <div className="ft-body px-2 pb-2 space-y-2">{children}</div>}
    </div>
  );
}

function MediaPlanSummary({ state, totalKpiCount, estCreators, selectedSKUObj, progress, onGenerateCohort, generatingCohort }) {
  const { waveType, campaignName, budget, startDate, endDate, platforms, geoLevel, selectedStates, selectedDmas, zipCodes, commissionPct, selectedDeliverables, primaryCreative } = state;
  const accent = waveType === 'Performance' ? T : P;
  const progressColor = progress === 100 ? '#4ade80' : accent;
  const lineItem = [
    waveType ? `${waveType} Wave` : null,
    selectedSKUObj?.name?.split('–')[0].trim(),
    platforms.length > 0 ? platforms.join(' + ') : null,
    startDate && endDate ? `${startDate.slice(5).replace('-', '/')} → ${endDate.slice(5).replace('-', '/')}` : null,
    platforms.length > 0 ? `~${estCreators} creators` : null,
  ].filter(Boolean).join(' · ');
  const rows = [
    { l: 'Geo', v: formatGeoTarget(geoLevel, selectedStates, selectedDmas, zipCodes), c: '#fff' },
    ...(waveType === 'Performance' ? [
      { l: 'Commission', v: commissionPct || '10%', c: '#fff' },
      { l: 'KPIs · Deliverables', v: `${totalKpiCount > 0 ? totalKpiCount : 'Auto'} · ${selectedDeliverables.size > 0 ? selectedDeliverables.size : 'Auto'}`, c: '#fff' },
      { l: 'Proj. ROAS Range', v: '5.2x – 8.4x', c: '#4ade80' },
    ] : [
      { l: 'Primary Angle', v: primaryCreative || 'Auto-assigned', c: '#fff' },
      { l: 'KPIs Set', v: totalKpiCount > 0 ? `${totalKpiCount} metrics` : 'Auto', c: '#fff' },
      { l: 'Proj. Reach', v: '2.4M – 6.1M', c: '#4ade80' },
    ]),
  ];
  return (
    <div className="lg:col-span-3">
      <div className="rounded-2xl overflow-hidden sticky top-20" style={{ background: CARD, border: `1px solid ${accent}30`, boxShadow: `0 8px 40px ${accent}10` }} data-testid="media-plan-summary">
        <div className="p-4 pb-3" style={{ background: `linear-gradient(160deg, ${accent}0E, transparent 60%)` }}>
          <p className="text-[9px] uppercase tracking-[0.18em] font-black" style={{ color: accent }}>Your wave · one line item</p>
          <p className="text-3xl font-black text-white mt-1.5 tabular-nums" data-testid="one-line-item-budget">
            {budget ? `$${Number(budget).toLocaleString()}` : '$0'}
          </p>
          <p className="text-[10px] text-gray-400 leading-relaxed mt-1.5 min-h-[28px]" data-testid="one-line-item-summary">
            {lineItem || 'Fill the essentials on the left — this line item builds itself.'}
          </p>
          {campaignName && <p className="text-[9px] text-gray-600 mt-1 truncate">“{campaignName}”</p>}
        </div>

        <div className="px-4 pb-3">
          {rows.map(item => (
            <div key={item.l} className="flex items-start justify-between py-1.5 border-t" style={{ borderColor: BORDER }}>
              <span className="text-[9px] text-gray-600">{item.l}</span>
              <span className="text-[9px] font-semibold text-right ml-2 max-w-[58%]" style={{ color: item.c }}>{item.v}</span>
            </div>
          ))}
        </div>

        <div className="px-4 pb-4 space-y-2.5">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] text-gray-500">Brief completion</span>
              <span className="text-[9px] font-bold" style={{ color: progressColor }}>{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full" style={{ background: BG }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: progressColor }} />
            </div>
          </div>
          <button onClick={onGenerateCohort} disabled={progress < 60 || generatingCohort}
            className="w-full px-4 py-3 rounded-xl text-sm font-black text-black flex items-center justify-center gap-2 transition-all disabled:opacity-30"
            style={{ background: accent, boxShadow: `0 0 24px ${accent}35` }} data-testid="generate-cohort-btn">
            {generatingCohort ? <><Loader2 className="h-4 w-4 animate-spin" />Building Cohort...</> : <><ArrowRight className="h-4 w-4" />Continue to Cohort</>}
          </button>
          {progress < 60 && (
            <p className="text-[8px] text-gray-600 text-center">Complete the essentials to unlock your cohort.</p>
          )}
          <div className="flex gap-2">
            <button className="flex-1 px-3 py-2 rounded-lg text-[10px] font-semibold text-gray-500 hover:text-white transition-colors flex items-center justify-center gap-1" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="save-draft-btn">
              <Save className="h-3 w-3" />Save Draft
            </button>
            <button className="flex-1 px-3 py-2 rounded-lg text-[10px] font-semibold text-gray-500 hover:text-white transition-colors flex items-center justify-center gap-1" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="generate-pdf-btn">
              <FileText className="h-3 w-3" />Brief PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Main Step 1 Overview Component
   ────────────────────────────────────────────── */
export default function Step1Overview({ state, actions, progress, totalKpiCount, estCreators, selectedSKUObj, onGenerateCohort, generatingCohort, onApplyMoment, onActivated, contextStrip }) {
  const { waveType } = state;
  return (
    <div className="space-y-5" data-testid="step-overview">
      {/* ═══ HERO WAVE-TYPE SELECTOR ═══
          First and most important decision in planning — rendered bigger,
          with a prompt banner until the brand picks one so it reads like a
          CTA not a form field. Collapses to a compact confirmation strip
          once chosen. */}
      {!waveType && (
        <div className="rounded-2xl p-5 lg:p-6 relative overflow-hidden"
             style={{
               background: 'linear-gradient(135deg, rgba(0,245,255,0.06), rgba(255,107,189,0.04), #121212)',
               border: `1px solid ${T}30`,
             }}
             data-testid="wave-type-hero">
          <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full blur-3xl opacity-25" style={{ background: T }} />
          <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: P }} />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-1">Step 1 of 3 · Start here</p>
            <h2 className="text-2xl lg:text-3xl font-black text-white leading-tight mb-2">What's this wave for?</h2>
            <p className="text-xs lg:text-sm text-gray-400 leading-relaxed mb-5 max-w-2xl">
              Pick one to unlock the right KPIs, creator cohort, and burst plan.
              You can always change your mind later — but this decision shapes
              everything downstream.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wave-type-selector">
              {[
                { type: 'Performance', icon: TrendingUp, tagline: 'Sell more.', desc: 'Drive sales, ROAS & conversions via ShopMy + TikTok Shop affiliate links.', accent: T, kpis: ['ROAS', 'CPA', 'Units sold', 'ShopMy CTR'] },
                { type: 'Branding', icon: Heart, tagline: 'Be remembered.', desc: 'Build awareness, engagement & long-tail community love on the feed.', accent: P, kpis: ['Reach', 'Engagement rate', 'Sentiment', 'Share rate'] },
              ].map(w => (
                <button key={w.type} onClick={() => actions.setWaveType(w.type)}
                        data-testid={`select-${w.type.toLowerCase()}-wave`}
                        className="p-6 rounded-2xl text-left transition-all duration-300 relative overflow-hidden group hover:-translate-y-0.5"
                        style={{
                          background: '#0A0A0A',
                          border: `2px solid ${BORDER}`,
                          boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = w.accent;
                          e.currentTarget.style.boxShadow = `0 12px 40px ${w.accent}35`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = BORDER;
                          e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.45)';
                        }}>
                  <div className="absolute -top-10 -right-8 w-28 h-28 rounded-full blur-2xl opacity-30 group-hover:opacity-50 transition-opacity" style={{ background: w.accent }} />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                         style={{ background: `${w.accent}18`, boxShadow: `0 0 24px ${w.accent}30` }}>
                      <w.icon className="h-7 w-7" style={{ color: w.accent }} />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-1" style={{ color: w.accent }}>{w.type} Wave</p>
                    <h3 className="text-xl lg:text-2xl font-black text-white mb-1.5">{w.tagline}</h3>
                    <p className="text-[11px] text-gray-400 leading-relaxed mb-4">{w.desc}</p>
                    <div className="flex gap-1.5 flex-wrap mb-4">
                      {w.kpis.map(k => (
                        <span key={k} className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: `${w.accent}10`, color: w.accent, border: `1px solid ${w.accent}30` }}>{k}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color: w.accent }}>
                      Pick this wave <ArrowRightCircle className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {waveType && (
        <div className="rounded-xl p-4 flex items-center gap-3" data-testid="wave-type-chosen-strip"
             style={{
               background: waveType === 'Performance' ? `${T}08` : `${P}08`,
               border: `1px solid ${waveType === 'Performance' ? T : P}40`,
             }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: `${waveType === 'Performance' ? T : P}18` }}>
            {waveType === 'Performance' ? <TrendingUp className="h-5 w-5" style={{ color: T }} /> : <Heart className="h-5 w-5" style={{ color: P }} />}
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: waveType === 'Performance' ? T : P }}>{waveType} Wave</p>
            <p className="text-[11px] text-gray-500">
              {waveType === 'Performance'
                ? 'Optimising for sales, ROAS and affiliate conversions.'
                : 'Optimising for reach, engagement and community sentiment.'}
            </p>
          </div>
          <button onClick={() => actions.setWaveType('')} data-testid="change-wave-type-btn"
                  className="text-[10px] text-gray-400 hover:text-white font-bold px-3 py-1.5 rounded-lg border border-transparent hover:border-[#2A2A2A] transition-all">
            Change
          </button>
        </div>
      )}

      {/* Context strip — Next Move + Live Campaigns (only before a wave type is selected) */}
      {!waveType && contextStrip}

      {waveType && (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
          <div className="lg:col-span-7 space-y-4">
            <CommonFields state={state} actions={actions} selectedSKUObj={selectedSKUObj} />
            <PlatformPicker platforms={state.platforms} togglePlatform={actions.togglePlatform} accent={waveType === 'Performance' ? T : P} />

            {/* Fine-tune — everything below is optional. Smart defaults are
                already applied; each accordion header shows the live value
                so the brand only opens what it wants to change. */}
            <div className="pt-1" data-testid="fine-tune-group">
              <p className="text-[9px] uppercase tracking-widest text-gray-600 font-bold px-1 mb-2">
                Fine-tune <span className="normal-case tracking-normal font-medium text-gray-700">· optional — smart defaults already applied</span>
              </p>
              <div className="space-y-2">
                <FineTune title="Where it runs" icon={MapPin} accent={T} testId="finetune-geo"
                          summary={formatGeoTarget(state.geoLevel, state.selectedStates, state.selectedDmas, state.zipCodes)}>
                  <GeoTargetingSection state={state} actions={actions} />
                </FineTune>

                {waveType === 'Performance' && (
                  <>
                    <FineTune title="Creator incentives" icon={DollarSign} accent={T} testId="finetune-incentives"
                              summary={`${state.commissionPct || '10%'} base commission${state.boostTrack && state.boostTrack !== 'none' ? ' + performance boost' : ''}`}>
                      <IncentivesPanel state={state} actions={actions} />
                    </FineTune>
                    <FineTune title="KPI targets & deliverables" icon={BarChart3} accent={T} testId="finetune-kpis"
                              summary={`${totalKpiCount > 0 ? `${totalKpiCount} KPIs` : 'Auto KPIs'} · ${state.selectedDeliverables.size > 0 ? `${state.selectedDeliverables.size} deliverables` : 'creators pick the format'}`}>
                      <PerformanceKpisDeliverables state={state} actions={actions} estCreators={estCreators} />
                    </FineTune>
                  </>
                )}

                {waveType === 'Branding' && (
                  <FineTune title="KPI targets & creative direction" icon={Eye} accent={P} testId="finetune-creative"
                            summary={`${totalKpiCount > 0 ? `${totalKpiCount} KPIs` : 'Auto KPIs'} · ${state.primaryCreative || 'angle auto-assigned per creator'}`}>
                    <BrandingSection state={state} actions={actions} />
                  </FineTune>
                )}

                <FineTune title="Tentpole moments" icon={CalendarDays} accent={waveType === 'Performance' ? T : P} testId="finetune-tentpoles"
                          summary="Ride a cultural moment — auto-fills dates, hooks and creators">
                  <TentpoleMomentsSection onApplyMoment={onApplyMoment} onActivated={onActivated} />
                </FineTune>
              </div>
            </div>
          </div>
          <MediaPlanSummary state={state} totalKpiCount={totalKpiCount} estCreators={estCreators} selectedSKUObj={selectedSKUObj}
                            progress={progress} onGenerateCohort={onGenerateCohort} generatingCohort={generatingCohort} />
        </div>
      )}
    </div>
  );
}
